import FlowsBillableSwitch from 'components/Flow/FlowsBillableSwitch';
import PermissionService from 'components/Common/PermissionService';
import React, { useContext, useMemo } from 'react';
import {
  Divider, Stack, Tooltip,
} from '@mui/material';
import { Link as DomLink } from 'react-router-dom';
import { FlowBuilderContext } from 'pages/Flow/FlowBuilder/FlowBuilderContextProvider';
import { HButton } from 'BaseComponents';
import { HelpFlow } from 'pages/Flow/HelpFlow';
import { userPermissions } from 'utils/appConstants';


const CloneAction = ({ onClick }) => (
  <Tooltip
    title='Creates a complete copy of this flow, including all version history up to this point. The clone will contain this version and all previous versions.'
    placement='bottom'
  >
    <HButton
      onClick={onClick}
      variant='outlined'
      size='small'
    >
      Clone
    </HButton>
  </Tooltip>
);

const NavigationActions = ({ goBackUrl }) => (
  <Stack direction='row' gap={1} alignItems='center'>
    <HelpFlow />
    <HButton
      variant='outlined'
      to={goBackUrl}
      component={DomLink}
      size='small'
    >
      Go Back
    </HButton>
  </Stack>
);

const EditModeActions = ({
  handParametersDrawerOpen,
  handleCloneFlowDialogOpen,
  handleSaveAsNewVersionDialogOpen,
  isBillable,
  setIsBillable,
  canSeeBillableToggle,
}) => {
  const canEditFlows = PermissionService.isAuthorized([userPermissions.crFlowsBuilder]);

  const handleBillableToggle = (event) => {
    setIsBillable(event.target.checked);
  };

  return (
    <Stack direction='row' gap={1} alignItems='center'>
      {canEditFlows && canSeeBillableToggle && (
        <FlowsBillableSwitch
          isBillable={isBillable}
          onToggle={handleBillableToggle}
        />
      )}

      {canEditFlows && (
        <Tooltip
          title='Configure flow parameters/macros'
          placement='bottom'
        >
          <HButton
            onClick={handParametersDrawerOpen}
            variant='outlined'
            size='small'
          >
            Parameters
          </HButton>
        </Tooltip>
      )}

      {canEditFlows && (
        <CloneAction onClick={handleCloneFlowDialogOpen} />
      )}

      <Divider orientation='vertical' flexItem sx={{ height: 36 }} />

      {canEditFlows && (
        <Stack direction='row' gap={1}>
          <Tooltip
            title='Creates a new version of this flow with all your current changes. This will increment the version number (e.g., from v6 to v7).'
            placement='bottom'
          >
            <HButton
              variant='outlined'
              onClick={handleSaveAsNewVersionDialogOpen}
              size='small'
            >
              Save as New Version
            </HButton>
          </Tooltip>

          <Tooltip
            title='Saves the flow and checks if this flow is ready to run or if datasets need to be assigned.'
            placement='bottom'
          >
            <HButton
              color='primary'
              variant='contained'
              type='submit'
              size='small'
            >
              Update
            </HButton>
          </Tooltip>
        </Stack>
      )}
    </Stack>
  );
};

const ViewModeActions = ({
  isViewingDifferentVersion,
  cleanRoomFlow,
  flow,
  handleUseFlowVersion,
}) => (
  <Stack direction='row' gap={1} alignItems='center'>
    {isViewingDifferentVersion && (
      <Tooltip
        title={`Make version ${flow.version} the current active version (replacing v${cleanRoomFlow.flowVersion}). This version will be used for new flow runs.`}
        placement='bottom'
      >
        <HButton
          color='primary'
          variant='contained'
          onClick={() => handleUseFlowVersion(flow.version)}
          size='small'
        >
          Use Version
        </HButton>
      </Tooltip>
    )}
  </Stack>
);

const FlowActionButtons = () => {
  const {
    editMode,
    handParametersDrawerOpen,
    goBackUrl,
    flow,
    cleanRoomFlow,
    handleUseFlowVersion,
    handleSaveAsNewVersionDialogOpen,
    handleCloneFlowDialogOpen,
    isBillable,
    setIsBillable,
    canSeeBillableToggle,
  } = useContext(FlowBuilderContext);
  const canViewVersions = PermissionService.isAuthorized([
    userPermissions.crFlowsBuilder,
    userPermissions.crManageFlowRuns,
    userPermissions.manageDatasets,
    userPermissions.crViewOnlyFlows,
  ]);
  const isViewingDifferentVersion = useMemo(() => (
    flow.version !== cleanRoomFlow?.flowVersion
  ), [flow, cleanRoomFlow]);

  return (
    <Stack direction={editMode ? 'column' : 'row'} gap={1} alignItems='flex-end'>
      <NavigationActions goBackUrl={goBackUrl} />

      {editMode && (
        <EditModeActions
          handParametersDrawerOpen={handParametersDrawerOpen}
          handleCloneFlowDialogOpen={handleCloneFlowDialogOpen}
          handleSaveAsNewVersionDialogOpen={handleSaveAsNewVersionDialogOpen}
          isBillable={isBillable}
          setIsBillable={setIsBillable}
          canSeeBillableToggle={canSeeBillableToggle}
        />
      )}

      {isViewingDifferentVersion && canViewVersions && (
        <ViewModeActions
          isViewingDifferentVersion={isViewingDifferentVersion}
          cleanRoomFlow={cleanRoomFlow}
          flow={flow}
          handleUseFlowVersion={handleUseFlowVersion}
          handleCloneFlowDialogOpen={handleCloneFlowDialogOpen}
        />
      )}
    </Stack>
  );
};

export default FlowActionButtons;
